import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { RichTextProvider } from "reactjs-tiptap-editor";
import { Heading, RichTextHeading } from "reactjs-tiptap-editor/heading";
import "reactjs-tiptap-editor/style.css";
import { Button, ScrollArea } from "@devhop/ui";
import {
  BoldIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect } from "react";

type SimpleRichTextEditorProps = {
  /** ສ້າງ editor ໃໝ່ເມື່ອປ່ຽນຟິວ / ໂຫຼດຄ່າ (ໃຊ້ field.id ເປັນ resetKey) */
  resetKey: string;
  initialHtml: string;
  onHtmlChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  minHeightClassName?: string;
};

export function SimpleRichTextEditor({
  resetKey,
  initialHtml,
  onHtmlChange,
  disabled,
  placeholder = "ຂຽນຄຳອະທິບາຍບລັອກນີ້…",
  minHeightClassName = "min-h-[140px]",
}: SimpleRichTextEditorProps) {
  const editor = useEditor(
    {
      editable: !disabled,
      extensions: [
        StarterKit.configure({
          heading: false,
        }),
        Heading.configure({
          levels: [1, 2, 3, 4, 5, 6],
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            rel: "noopener noreferrer",
            target: "_blank",
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
      ],
      content: initialHtml.trim() ? initialHtml : "<p></p>",
      editorProps: {
        attributes: {
          class: `${minHeightClassName} px-3 py-2 text-sm leading-relaxed focus:outline-none [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:font-semibold [&_h1]:text-lg [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_h4]:text-sm [&_h4]:font-semibold [&_h5]:font-medium [&_h5]:text-sm [&_h6]:font-medium [&_h6]:text-xs`,
        },
      },
      onUpdate: ({ editor: ed }) => {
        onHtmlChange(ed.getHTML());
      },
    },
    [resetKey],
  );

  useLayoutEffect(() => {
    if (!editor) return;
    const want = initialHtml.trim() ? initialHtml : "<p></p>";
    const cur = editor.getHTML();
    if (cur === want) return;
    if (editor.isFocused) return;
    editor.commands.setContent(want, { emitUpdate: false });
  }, [editor, initialHtml]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  const runLink = useCallback(() => {
    if (!editor || disabled) return;
    const prevUrl = editor.getAttributes("link").href as string | undefined;
    const raw = window.prompt("URL", prevUrl ?? "");
    if (raw === null) return;
    const url = raw.trim();
    if (!url.length) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className={`flex ${minHeightClassName} items-center justify-center rounded-md border bg-muted/40 text-muted-foreground text-sm`}>
        ກຳລັງໂຫຼດຕົວແກ້…
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-input bg-background">
      <RichTextProvider editor={editor}>
        <div className="flex shrink-0 flex-wrap items-center gap-1 border-border border-b bg-muted/30 p-2">
          <Button
            type="button"
            variant={editor.isActive("bold") ? "default" : "outline"}
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            title="ໜາ"
            aria-label="ໜາ"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant={editor.isActive("italic") ? "default" : "outline"}
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            title="ຕົວອຽງ"
            aria-label="ຕົວອຽງ"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon className="h-4 w-4" aria-hidden />
          </Button>
          <div className="inline-flex shrink-0 items-center [&_button]:h-8">
            <RichTextHeading />
          </div>
          <Button
            type="button"
            variant={editor.isActive("bulletList") ? "default" : "outline"}
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            title="ລາຍການຈຸດ"
            aria-label="ລາຍການຈຸດ"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <ListIcon className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant={editor.isActive("orderedList") ? "default" : "outline"}
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            title="ລາຍການລຳດັບ"
            aria-label="ລາຍການລຳດັບ"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrderedIcon className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant={editor.isActive("link") ? "default" : "outline"}
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            title="ລິ້ງ"
            aria-label="ລິ້ງ"
            onClick={() => runLink()}
          >
            <LinkIcon className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <ScrollArea className="max-h-[min(45vh,380px)] w-full overflow-auto">
          <EditorContent
            editor={editor}
            className="[&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:pb-3 [&_.ProseMirror]:focus:outline-none"
          />
        </ScrollArea>
      </RichTextProvider>
    </div>
  );
}
